import { Helper } from './helper';
import { TranslateService } from '@ngx-translate/core';
import { merge, fill, get, isEmpty } from 'lodash-es';
import { SCREEN_SIZE } from './enums';

declare let Chart: any;
const mobileFontSize = 10;
const desktopFontSize = 14;
const COLORS = {
  black: '#000',
  blue: '#007DC3',
  darkBlue: '#004E9B',
  lightBlue: '#A4D7F4',
  red: '#DC1C2E',
  gray: '#5F6062',
  lightGray: '#949CA1',
  darkGray: '#414042'
}

export type DemographicType = 'income' | 'education_level' | 'housing_tenancy' | 'family_composition' | 'housing_type' | 'population_age' | 'mother_tongue' | 'commute_mode';
export interface IDemographicsResponse {
  data: {
    type: string;
    attributes: any;
  }
}
export interface IFormattedValue {
  type: 'percent' | 'average' | 'standalone' | 'unknown';
  value: number;
  text: string;
}
export class Demographics {
  income?: {
    avg_household_income?: IFormattedValue;
    avg_individual_income?: IFormattedValue;
  };

  education_level?: {
    no_high_school?: IFormattedValue;
    high_school?: IFormattedValue;
    trade_certificate?: IFormattedValue;
    college_certificate?: IFormattedValue;
    university_certificate?: IFormattedValue;
    bachelor_degree?: IFormattedValue;
    post_graduate_degree?: IFormattedValue;
  };

  housing_tenancy?: {
    owners?: IFormattedValue;
    renters?: IFormattedValue;
  };

  commute_mode?: {
    bicycle?: IFormattedValue;
    foot?: IFormattedValue;
    other?: IFormattedValue;
    transit?: IFormattedValue;
    drive?: IFormattedValue;
  }

  family_composition?: {
    familiesWithChildren?: IFormattedValue;
    children_1?: IFormattedValue;
    children_3plus?: IFormattedValue;
    children_2?: IFormattedValue;
    children_0?: IFormattedValue;
  }

  household_composition?: {
    multi_family?: IFormattedValue;
    multi_person?: IFormattedValue;
    single_family?: IFormattedValue;
    single_person?: IFormattedValue;
  }

  housing_type?: {
    apartment_1_to_4_floors?: IFormattedValue;
    apartment_5_plus_floors?: IFormattedValue;
    duplex?: IFormattedValue;
    row_houses?: IFormattedValue;
    semi_detached?: IFormattedValue;
    single_detached?: IFormattedValue;
  }

  official_language_knowledge?: {
    en_and_fr?: IFormattedValue;
    en_only?: IFormattedValue;
    fr_only?: IFormattedValue;
    other?: IFormattedValue;
  }

  population_age?: {
    v0_to_4?: IFormattedValue;
    v5_to_9?: IFormattedValue;
    v10_to_14?: IFormattedValue;
    v15_to_19?: IFormattedValue;
    v20_to_34?: IFormattedValue;
    v35_to_49?: IFormattedValue;
    v50_to_64?: IFormattedValue;
    v65_to_79?: IFormattedValue;
    v80_plus?: IFormattedValue;
  }

  mother_tongue?: {
    topLanguages?: Array<{ name: string, value: number }>
  }

  demographicResponseV2Keys: string[] = [
    'income',
    'housing_type',
    'education_level',
    'commute_mode',
    'household_composition',
    'housing_tenancy',
    'official_language_knowledge'
  ];

  constructor(data: IDemographicsResponse) {
    if (isEmpty(data)) return;

    this.assignCategoryVariablesFromDemographicData(data);
    this.populatePopulationAgeData(data);
    this.populateFamilyCompositionData(data);
    this._fillMotherTongue(data);
  }

  //TODO, extract into separate function that takes the field name
  private populateFamilyCompositionData(data: IDemographicsResponse): void {
    const category = get(data, `data.attributes.family_composition`, null);
    const categoryVariableList = category['variables'];
    if (categoryVariableList) { 
      this.populateCategorySubFields('family_composition', categoryVariableList, category['type']);
    }
    this.defineFamiliesWithChildrenField(category['type']);    
  }

  private defineFamiliesWithChildrenField(parentType: IFormattedValue['type']): void {
    let familiesWithChildrenValue: number = 100 - this.family_composition.children_0.value;
    if (parentType === 'percent') familiesWithChildrenValue = familiesWithChildrenValue / 100;

    this.family_composition.familiesWithChildren = this.getFormattedResult({value: familiesWithChildrenValue, type: parentType, text: ''}, parentType);
  }

  private assignCategoryVariablesFromDemographicData(demographicData: IDemographicsResponse): void {
    this.demographicResponseV2Keys.forEach(categoryName => {
      const category = get(demographicData, `data.attributes.${categoryName}`, null);
      const categoryVariableList = category['variables'];
      if (categoryVariableList) { 
        this.populateCategorySubFields(categoryName, categoryVariableList, category['type']);
      }
    });
  }

  private populateCategorySubFields(categoryName: string, categoryVariableList: string[], categoryType: IFormattedValue['type'], fieldNamePrefixModifier: string = ''): void {
    this[categoryName] = {};
    for(let subField in categoryVariableList) {
      const variableName = fieldNamePrefixModifier + categoryVariableList[subField]['variable'];
      this[categoryName][variableName] = this.getFormattedResult(categoryVariableList[subField], categoryType);
    };
  }

  private getFormattedResult(categorySubField: any, parentCategoryType: IFormattedValue['type']): IFormattedValue {
    if (!this.isCategorySubFieldDefined(categorySubField) || !parentCategoryType) return { type: 'unknown', text: '-', value: 0};
    
    const result: IFormattedValue = { value: categorySubField.value, text: '', type: 'unknown' };
    result.type = parentCategoryType;
    
    switch (parentCategoryType) {
      case 'percent': {
        result.value = Helper.decimalToPercent(categorySubField.value);
        result.text = this.getFormatted(categorySubField.value, 'percent');
        break;
      }
      case 'standalone': {
        result.type = 'average';
        result.text = this.getFormatted(categorySubField.value, 'average');
        break;
      }
      default: {
        result.text = categorySubField.value.toFixed(2);
        break;
      }
    }
    return result;
  }

  private isCategorySubFieldDefined(categorySubField: any): boolean {
    return categorySubField && categorySubField.value != null && typeof categorySubField.value != 'undefined';
  }

  private populatePopulationAgeData(data: IDemographicsResponse): void {
      const category = get(data, `data.attributes.population_age`, null);
      const categoryVariableList = category['variables'];
      if (categoryVariableList) { 
        this.populateCategorySubFields('population_age', categoryVariableList, category['type'], 'v');
      }
  }

  private _fillMotherTongue(data: IDemographicsResponse) {
    const topLanguages = get(data, 'data.attributes.mother_tongue.variables', null);
    if (topLanguages && topLanguages.length && topLanguages.map) {
      this.mother_tongue = { topLanguages: topLanguages.map(subField => ({ name: subField.variable, value: subField.value })) };
    }
  }

  hasAnyData() {
    return this.education_level || this.income || this.housing_tenancy || this.commute_mode ||
      this.family_composition || this.household_composition || this.housing_type || this.mother_tongue || this.commute_mode;
  }

  getFormatted(value: number, type: 'percent' | 'average') {
    return type === 'average' ? Helper.formatPrice(value) : (Helper.decimalToPercent(value).toFixed(2) + '%');
  }
}

const percentAxis = {
  ticks: {
    stepSize: 20,
    max: 100,
    callback: function (value, index, values) {
      return value + '%';
    }
  }
};

export class DemographicChartHelper {
  public static getBaseChartConfig(chartType: Chart.ChartType, chartDataSets: Chart.ChartDataSets[], chartLabels: string[]): Chart.ChartConfiguration {
    const chartData: Chart.ChartData = {
      datasets: chartDataSets,
      labels: chartLabels
    };
    return {
      type: chartType,
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        events: [],
        tooltips: {
          enabled: false
        },
        title: {
          display: false
        },
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ]
        }
      }
    };
  }

  public static getBaseBarChartConfig(chartDataSets: Chart.ChartDataSets[], chartLabels: string[], screenSize: SCREEN_SIZE, isHorizontal = false): Chart.ChartConfiguration {
    const config = this.getBaseChartConfig(isHorizontal ? 'horizontalBar' : 'bar', chartDataSets, chartLabels);
    const fontSize = screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? desktopFontSize : mobileFontSize;
    return merge({}, config, {
      options: {
        animation: {
          onComplete: function () {
            const chartInstance = this.chart,
              ctx = chartInstance.ctx as CanvasRenderingContext2D;
            ctx.font = (screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? '24px' : '12px') + ' Roboto';
            ctx.textAlign = isHorizontal ? 'left' : 'center';
            ctx.textBaseline = 'middle';
            const varType = this.chart.config.options.title.text[1], demoTitle = this.chart.config.options.title.text[0];

            this.data.datasets.forEach(function (dataset, i) {
              const meta = chartInstance.controller.getDatasetMeta(i);
              meta.data.forEach(function (bar, index) {
                const textPositionX = bar._model.x + (isHorizontal ? screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 10 : 5 : demoTitle === 'household-income' ? 10 : 0),
                  textPositionY = bar._model.y - (isHorizontal ? 0 : screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 20 : 10);
                const data = dataset.data[index];
                if (varType === 'currency') {
                  const value = Helper.numberWithCommas(data.toFixed(2));
                  ctx.fillStyle = COLORS.black;
                  const preffixWidth = ctx.measureText('$').width;
                  const textWidth = ctx.measureText(value).width;
                  ctx.fillText('$', textPositionX - preffixWidth - (textWidth / 2), textPositionY);
                  ctx.fillStyle = COLORS.red;
                  ctx.fillText(value, textPositionX, textPositionY);
                } else {
                  const value = Math.round(data).toString();
                  ctx.fillStyle = COLORS.red;
                  ctx.fillText(value, textPositionX, textPositionY);
                  ctx.fillStyle = COLORS.black;
                  const suffixWidth = ctx.measureText(value).width;
                  ctx.fillText('%', textPositionX + suffixWidth, textPositionY);
                }
              });
            });
          }
        },
        layout: {
          padding: {
            top: screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 45 : 20,
            bottom: screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 45 : 20,
            left: 10, right: 10
          }
        },
        legend: {
          display: false,
          position: (screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 'right' : 'bottom'),
          labels: {
            fontSize,
            fontFamily: 'Roboto',
            fontColor: '#3C96C2'
          }
        },
        legendCallback: function (chart) {
          const text = [];
          text.push(`<ul style="font-size: ${fontSize}px; list-style: none;margin-top: 2em;">`);
          for (let i = 0; i < chart.data.datasets[0].data.length; i++) {
            text.push('<li class="is-flex is-flex-row flex-one has-flex-align-center" style="min-height: 2.5em; margin-bottom: .5em; margin-right: .5em;">');
            text.push('<span style="flex-shrink:0; margin-right: 3px; background-color:' + chart.data.datasets[0].backgroundColor[i] + '; width: 24px; height: 24px;"></span> ');
            if (chart.data.labels[i]) {
              text.push('<span>', chart.data.labels[i], '</span>');
            }
            text.push('</li>');
          }
          text.push('</ul>');
          return text.join('');
        },
        scales: {
          xAxes: [
            {
              gridLines: { color: 'black' },
              maxBarThickness: screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 45 : 30,
              display: true,
              color: 'green',
              ticks: { color: 'black' }
            }
          ],
          yAxes: [
            {
              maxBarThickness: screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 45 : 30,
              gridLines: { color: 'black' },
              ticks: {
                min: 0,
              },
              color: 'black'
            }
          ]
        },

      }
    }) as Chart.ChartConfiguration;
  }

  public static getBaseDoughnutChartConfig(chartDataSets: Chart.ChartDataSets[], chartLabels: string[], screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const config = DemographicChartHelper.getBaseChartConfig('doughnut', chartDataSets, chartLabels);
    const fontSize = (screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? desktopFontSize : mobileFontSize);

    return merge({}, config, {
      options: {
        cutoutPercentage: 80,
        layout: {
          padding: 30
        },
        legend: {
          display: false,
          labels: {
            boxWidth: 15,
            fontSize: (screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP ? 14 : 10),
            fontFamily: 'Roboto',
            fontColor: 'black'
          }
        },
        legendCallback: function (chart) {
          const text = [];
          text.push(`<ul style="font-size: ${fontSize}px; list-style: none;margin-top: 2em;">`);
          for (let i = 0; i < chart.data.datasets[0].data.length; i++) {
            text.push('<li class="is-flex is-flex-row flex-one has-flex-align-center" style="min-height: 2.5em; margin-bottom: .5em; margin-right: .5em;">');
            text.push('<span style="flex-shrink:0; margin-right: 3px; background-color:' + chart.data.datasets[0].backgroundColor[i] + '; width: 24px; height: 24px;"></span> ');
            if (chart.data.labels[i]) {
              text.push('<span>', chart.data.labels[i], '</span>');

            }
            text.push('</li>');
          }
          text.push('</ul>');
          return text.join('');
        },
        scales: {
          xAxes: [
            {
              display: false
            }
          ],
          yAxes: [
            {
              display: false

            }
          ]
        },
      }
    }) as Chart.ChartConfiguration;
  }

  public static getTenancyChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.housing_tenancy.owners.value,
        demographics.housing_tenancy.renters.value
      ],
      backgroundColor: [
        COLORS.red,
        COLORS.lightBlue
      ]
    };
    const chartLabels = [
      `${demographics.housing_tenancy.owners.text} ${translateService.instant('demographics.household-tenancy.own')}`,
      `${demographics.housing_tenancy.renters.text} ${translateService.instant('demographics.household-tenancy.rent')}`,
    ];
    return merge(DemographicChartHelper.getBaseDoughnutChartConfig([chartDs], chartLabels, screenSize), {
      options: {
        legend: {
          position: 'right'
        },
        layout: {
          padding: 10
        }
      }
    });
  }

  public static getHouseholdIncomeChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.income.avg_household_income.value,
        demographics.income.avg_individual_income.value
      ],
      backgroundColor: [
        COLORS.darkBlue,
        COLORS.red
      ]
    };
    const chartLabels = [
      `${translateService.instant('demographics.household-income.average-household-income')}`.toUpperCase(),
      `${translateService.instant('demographics.household-income.average-individual-income')}`.toUpperCase(),
    ];

    return merge(DemographicChartHelper.getBaseBarChartConfig([chartDs], chartLabels, screenSize), {
      options: {
        title: {
          text: ['household-income', 'currency']
        },
        scales: {
          xAxes: [{
            ticks: {
              display: screenSize >= SCREEN_SIZE.DESKTOP_UP
            }
          }],
          yAxes: [{
            ticks: {
              maxTicksLimit: 5,
              // Include a dollar sign in the ticks
              callback: function (value, index, values) {
                return '$' + Helper.numberWithCommas(value);
              }
            }
          }]
        }
      }
    });
  }

  public static getPopulationAgeChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.population_age.v0_to_4.value,
        demographics.population_age.v5_to_9.value,
        demographics.population_age.v10_to_14.value,
        demographics.population_age.v15_to_19.value,
        demographics.population_age.v20_to_34.value,
        demographics.population_age.v35_to_49.value,
        demographics.population_age.v50_to_64.value,
        demographics.population_age.v65_to_79.value,
        demographics.population_age.v80_plus.value
      ],
      backgroundColor: [
        COLORS.darkBlue,
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue
      ]
    };
    const chartLabels = [
      `${translateService.instant('demographics.population-age.v0-4')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v5-9')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v10-14')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v15-19')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v20-34')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v35-49')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v50-64')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v65-79')}`.toUpperCase(),
      `${translateService.instant('demographics.population-age.v80-plus')}`.toUpperCase(),
    ];
    return merge(DemographicChartHelper.getBaseBarChartConfig([chartDs], chartLabels, screenSize), {
      options: {
        title: { text: ['population-age', 'percentage'] },
        scales: {
          yAxes: [{ display: false }]
        }
      }
    });
  }

  public static getHousingTypeChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.housing_type.single_detached.value,
        demographics.housing_type.semi_detached.value,
        demographics.housing_type.row_houses.value,
        demographics.housing_type.duplex.value,
        demographics.housing_type.apartment_1_to_4_floors.value,
        demographics.housing_type.apartment_5_plus_floors.value
      ],
      backgroundColor: [
        COLORS.red,
        COLORS.lightGray,
        COLORS.lightBlue,
        COLORS.gray,
        COLORS.darkGray,
        COLORS.darkBlue
      ]
    };
    const chartLabels = [
      `${demographics.housing_type.single_detached.text} ${translateService.instant('demographics.housing-type.single-detached-house')}`.toUpperCase(),
      `${demographics.housing_type.semi_detached.text} ${translateService.instant('demographics.housing-type.semi-detached-house')}`.toUpperCase(),
      `${demographics.housing_type.row_houses.text} ${translateService.instant('demographics.housing-type.row-house')}`.toUpperCase(),
      `${demographics.housing_type.duplex.text} ${translateService.instant('demographics.housing-type.duplex')}`.toUpperCase(),
      `${demographics.housing_type.apartment_1_to_4_floors.text} ${translateService.instant('demographics.housing-type.apartment-less-than-5-floors')}`.toUpperCase(),
      `${demographics.housing_type.apartment_5_plus_floors.text} ${translateService.instant('demographics.housing-type.apartment-5-or-more-floors')}`.toUpperCase(),
    ];
    return merge(DemographicChartHelper.getBaseDoughnutChartConfig([chartDs], chartLabels, screenSize), { options: {} });
  }

  public static getEducationChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.education_level.no_high_school.value,
        demographics.education_level.high_school.value,
        demographics.education_level.college_certificate.value,
        demographics.education_level.trade_certificate.value,
        demographics.education_level.university_certificate.value,
        demographics.education_level.bachelor_degree.value,
        demographics.education_level.post_graduate_degree.value,
      ],
      backgroundColor: [
        COLORS.red,
        COLORS.darkBlue,
        COLORS.lightGray,
        COLORS.blue,
        COLORS.darkGray,
        COLORS.lightBlue,
        COLORS.gray,
      ]
    };
    const chartLabels = [
      `${demographics.education_level.no_high_school.text} ${translateService.instant('demographics.education.no-certificate')}`.toUpperCase(),
      `${demographics.education_level.high_school.text} ${translateService.instant('demographics.education.highschool-certificate')}`.toUpperCase(),
      `${demographics.education_level.college_certificate.text} ${translateService.instant('demographics.education.college-certificate')}`.toUpperCase(),
      `${demographics.education_level.trade_certificate.text} ${translateService.instant('demographics.education.apprentice-certificate')}`.toUpperCase(),
      `${demographics.education_level.university_certificate.text} ${translateService.instant('demographics.education.university-certificate')}`.toUpperCase(),
      `${demographics.education_level.bachelor_degree.text} ${translateService.instant('demographics.education.university-degree')}`.toUpperCase(),
      `${demographics.education_level.post_graduate_degree.text} ${translateService.instant('demographics.education.master-degree')}`.toUpperCase(),
    ];
    return DemographicChartHelper.getBaseDoughnutChartConfig([chartDs], chartLabels, screenSize);
  }

  public static getFamilyCompositionChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.family_composition.children_0.value,
        demographics.family_composition.familiesWithChildren.value,
        demographics.family_composition.children_1.value,
        demographics.family_composition.children_2.value,
        demographics.family_composition.children_3plus.value
      ],
      backgroundColor: [
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue,
        COLORS.darkBlue,
        COLORS.blue,
      ]
    };
    const chartLabels = [
      `${translateService.instant('demographics.family-composition.without-children')}`.toUpperCase(),
      `${translateService.instant('demographics.family-composition.with-children')}`.toUpperCase(),
      `${translateService.instant('demographics.family-composition.with-one-child')}`.toUpperCase(),
      `${translateService.instant('demographics.family-composition.with-two-children')}`.toUpperCase(),
      `${translateService.instant('demographics.family-composition.with-three-or-more-children')}`.toUpperCase(),
    ];
    return merge(DemographicChartHelper.getBaseBarChartConfig([chartDs], chartLabels, screenSize, true), {
      options: {
        title: { text: ['household-composition', 'percentage'] },
        scales: {
          xAxes: [percentAxis]
        }
      }
    });
  }

  public static getMotherTongueChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: demographics.mother_tongue.topLanguages.map(t => t.value * 100),
      backgroundColor: fill(Array(demographics.mother_tongue.topLanguages.length), COLORS.darkBlue).map((a, i) => i % 2 ? COLORS.blue : COLORS.darkBlue)
    };
    const chartLabels = demographics.mother_tongue.topLanguages.map(t => t.name.toUpperCase());
    const newChartLabels = DemographicChartHelper.labelInMultipleRow(chartLabels);
    return merge(DemographicChartHelper.getBaseBarChartConfig([chartDs], newChartLabels, screenSize, true), {
      options: {
        title: { text: ['mother-tongue', 'percentage'] },
        scales: {
          xAxes: [percentAxis]
        }
      }
    });
  }

  public static getCommuteModeChartConfig(demographics: Demographics, translateService: TranslateService, screenSize: SCREEN_SIZE): Chart.ChartConfiguration {
    const chartDs: Chart.ChartDataSets = {
      data: [
        demographics.commute_mode.bicycle.value,
        demographics.commute_mode.foot.value,
        demographics.commute_mode.drive.value,
        demographics.commute_mode.transit.value,
        demographics.commute_mode.other.value,
      ],
      backgroundColor: [
        COLORS.red,
        COLORS.blue,
        COLORS.lightBlue,
        COLORS.darkBlue,
        COLORS.darkGray,
      ]
    };
    const chartLabels = [
      `${demographics.commute_mode.bicycle.text} ${translateService.instant('demographics.commute-mode.bicycle')}`.toUpperCase(),
      `${demographics.commute_mode.foot.text} ${translateService.instant('demographics.commute-mode.foot')}`.toUpperCase(),
      `${demographics.commute_mode.drive.text} ${translateService.instant('demographics.commute-mode.car')}`.toUpperCase(),
      `${demographics.commute_mode.transit.text} ${translateService.instant('demographics.commute-mode.transit')}`.toUpperCase(),
      `${demographics.commute_mode.other.text} ${translateService.instant('demographics.commute-mode.otherMode')}`.toUpperCase(),
    ];
    return DemographicChartHelper.getBaseDoughnutChartConfig([chartDs], chartLabels, screenSize);
  }

  // takes the label and splits into smaller chunks if there is any ',' in the label
  private static labelInMultipleRow(labels: string[]): any[] {
    const newLabels = new Array();
    for (const index in labels) {
      if (labels[index].indexOf(',') > 0) {
        newLabels.push(DemographicChartHelper._consolidateMultilineLabels(labels[index].split(',')));
      } else {
        newLabels.push(labels[index]);
      }
    }
    return newLabels;
  }

  // Merge two lines if the upper line is longer
  private static _consolidateMultilineLabels(labels: string[]): string[] {
    if (labels.length > 2) {
      const newLabels = new Array();
      if (labels[0].length > labels[1].length + labels[2].length) {
        newLabels.push(labels[0]);
        newLabels.push(labels.slice(1, 3).join(',') + ')');
      }
      return newLabels;
    }

    return labels;
  }
}
